// @section: site-data
// LINECT サイトのコンテンツデータ・型・定数を集約

export const HERO_IMAGE =
  "https://skyagent-artifacts.skywork.ai/image/759e3541-5788-4a87-b269-5afc632c7c7a/4682922c-e91a-40cb-bbf2-fb1d0faf7766/prod_agent_759e3541-5788-4a87-b269-5afc632c7c7a/hero_crm_jp_2.png";

// お問い合わせフォーム（ATIRO）
export const CONTACT_URL = "https://form.atiro.co.jp/@atiro";

export const NAV_ITEMS = [
  { id: "about", label: "linectとは" },
  { id: "features", label: "機能" },
  { id: "effect", label: "導入効果" },
  { id: "cases", label: "活用事例" },
  { id: "flow", label: "導入の流れ" },
  { id: "pricing", label: "料金" },
  { id: "faq", label: "よくある質問" },
] as const;

// 活用事例（業種別）
export type Case = {
  no: string;
  industry: string;
  tag: string;
  challenge: string;
  solution: string;
  results: string[];
};

export const CASES: Case[] = [
  {
    no: "01",
    industry: "不動産",
    tag: "Real Estate",
    challenge:
      "来店・問い合わせ後の追客が属人化し、検討期間の長い見込み顧客を取りこぼしていた。",
    solution:
      "LINEで物件問い合わせを受付け、希望条件・検討状況を顧客カードに集約。新着物件や内見案内を条件に応じて自動配信。",
    results: ["反響後の追客率を改善", "内見予約のLINE完結", "担当引き継ぎ時の情報ロスを解消"],
  },
  {
    no: "02",
    industry: "旅行代理店",
    tag: "Travel Agency",
    challenge:
      "繁忙期の相談対応が電話・店頭に集中し、過去の旅行履歴や好みを踏まえた提案ができていなかった。",
    solution:
      "LINEで旅行相談・見積りを受付け、渡航履歴・同行者・予算をプロフィール化。出発前後のフォローや次回提案を自動化。",
    results: ["相談〜見積りをLINEで完結", "リピート提案の精度向上", "繁忙期の対応工数を削減"],
  },
  {
    no: "03",
    industry: "車ディーラー",
    tag: "Car Dealer",
    challenge:
      "商談から成約までの期間が長く、点検・車検・乗り替えタイミングのフォローが抜け漏れていた。",
    solution:
      "試乗予約・見積り相談をLINEで受付け、車両・点検履歴を一元管理。車検・点検時期に合わせたリマインドを自動配信。",
    results: ["試乗・来店予約のLINE化", "車検・点検の再来店を促進", "乗り替え提案の機会創出"],
  },
  {
    no: "04",
    industry: "美容院",
    tag: "Hair Salon",
    challenge:
      "予約は取れても再来店が定着せず、施術履歴や好みが担当スタイリスト個人に依存していた。",
    solution:
      "LINEで予約・カウンセリングを受付け、施術履歴・カラー・要望をカルテ化。次回来店目安に合わせてクーポンを自動配信。",
    results: ["再来店サイクルの定着", "会員ランク・ポイントで来店動機を強化", "スタッフ間で施術情報を共有"],
  },
];

export type Pillar = {
  no: string;
  title: string;
  desc: string;
};

// 3つの基盤機能（接客データの蓄積）
export const PILLARS: Pillar[] = [
  {
    no: "01",
    title: "接客・商談管理",
    desc: "店頭接客・商談・電話など、あらゆる対応履歴を一元的に記録。属人化を防ぎ、組織で顧客対応を継続する。",
  },
  {
    no: "02",
    title: "会員システム構築",
    desc: "LINE IDだけで登録できる会員基盤を構築。会員証・ポイント・クーポンで利用活性とロイヤリティを高める。",
  },
  {
    no: "03",
    title: "メッセージ配信",
    desc: "条件に応じた自動配信で、来店御礼や誕生日特典などのフォローアップを最適なタイミングで届ける。",
  },
];

export type Feature = {
  no: string;
  tag: string;
  title: string;
  desc: string;
  points: string[];
  image: string;
};

// 4つの主要機能特徴
export const FEATURES: Feature[] = [
  {
    no: "01",
    tag: "会員基盤",
    title: "LINE連携でオリジナルの会員組織を構築",
    desc: "LINE公式アカウントと連携し、LINE IDだけで登録できる自社会員組織を構築できます。",
    points: [
      "会員証・デジタル会員カードの発行",
      "ポイント・クーポンによる利用活性",
      "ランク制度で優良顧客を可視化",
    ],
    image:
      "https://skyagent-artifacts.skywork.ai/image/759e3541-5788-4a87-b269-5afc632c7c7a/9e2a31c5-1717-440c-8956-ba09896c5c0c/prod_agent_759e3541-5788-4a87-b269-5afc632c7c7a/feature_membership_2.png",
  },
  {
    no: "02",
    tag: "接客管理",
    title: "接客コミュニケーションを一元管理",
    desc: "LINEチャット・店頭接客・商談・電話など、あらゆるやり取りの履歴を登録・蓄積します。",
    points: [
      "対応履歴から接触頻度・ニーズを把握",
      "次回接客の質を担保する引き継ぎ",
      "チームでの対応状況をリアルタイム共有",
    ],
    image:
      "https://skyagent-artifacts.skywork.ai/image/759e3541-5788-4a87-b269-5afc632c7c7a/e28f0a86-1bc6-464a-8c32-04131ab1e6d1/prod_agent_759e3541-5788-4a87-b269-5afc632c7c7a/feature_communication_2.png",
  },
  {
    no: "03",
    tag: "顧客理解",
    title: "顧客情報を見える化し、寄り添う接客へ",
    desc: "嗜好性・検討状況・購入意欲など、接客に必要なデータを一元管理して可視化します。",
    points: [
      "顧客プロファイルを一画面で把握",
      "複数スタッフで情報共有し属人化を防止",
      "一人ひとりに最適化したパーソナル接客",
    ],
    image:
      "https://skyagent-artifacts.skywork.ai/image/759e3541-5788-4a87-b269-5afc632c7c7a/452c4e1e-8aaa-4005-b2fc-47a0a6a04e11/prod_agent_759e3541-5788-4a87-b269-5afc632c7c7a/feature_profile_2.png",
  },
  {
    no: "04",
    tag: "自動化",
    title: "自動配信で接客負荷を軽減",
    desc: "来店感謝・商談御礼・誕生日クーポンなど、条件ごとの接客を自動化して工数を削減します。",
    points: [
      "トリガー条件による自動メッセージ",
      "フォローアップの抜け漏れを防止",
      "スタッフの作業負荷を大幅に軽減",
    ],
    image:
      "https://skyagent-artifacts.skywork.ai/image/759e3541-5788-4a87-b269-5afc632c7c7a/296491a1-e403-4be6-ab5d-2d54922190c5/prod_agent_759e3541-5788-4a87-b269-5afc632c7c7a/feature_automation_2.png",
  },
];

export type Metric = {
  value: string;
  unit: string;
  label: string;
};

// 導入効果の指標（イメージ値であることを明示）
export const METRICS: Metric[] = [
  { value: "1", unit: "元管理", label: "接客・会員・配信データを統合" },
  { value: "LINE", unit: "起点", label: "新規登録のハードルを最小化" },
  { value: "属人化", unit: "防止", label: "対応履歴をチームで共有" },
];

export type FlowStep = {
  no: string;
  title: string;
  desc: string;
};

export const FLOW: FlowStep[] = [
  {
    no: "STEP 1",
    title: "お問い合わせ・要件ヒアリング",
    desc: "現状の課題や運用体制をヒアリングし、最適な活用イメージをご提案します。",
  },
  {
    no: "STEP 2",
    title: "会員基盤・接客フローの設計",
    desc: "LINE公式アカウント連携、会員ランク、配信シナリオなどを設計します。",
  },
  {
    no: "STEP 3",
    title: "導入・初期設定",
    desc: "会員システムやヒアリングシートを構築し、スタッフ向けの初期設定を行います。",
  },
  {
    no: "STEP 4",
    title: "運用開始・改善",
    desc: "蓄積データを分析し、接客品質と売上向上に向けた継続的な改善を支援します。",
  },
];

// 料金（元サイト掲載の情報に基づく）
export type Pricing = {
  price: string;
  unit: string;
  note: string;
  points: string[];
};

export const PRICING: Pricing = {
  price: "100,000",
  unit: "円 / 月（税別）",
  note: "linectは一律月額10万円でご提供しております。詳細はお問い合わせください。",
  points: [
    "接客・商談管理／会員システム構築／メッセージ配信をすべて利用可能",
    "店舗数・スタッフ数に応じた個別見積りにも対応",
    "導入設計・初期設定・運用改善まで伴走支援",
  ],
};

export type Faq = {
  q: string;
  a: string;
};

export const FAQS: Faq[] = [
  {
    q: "既存のLINE公式アカウントでも利用できますか？",
    a: "はい。お持ちのLINE公式アカウントと連携して会員基盤を構築できます。新規開設からのご相談も可能です。",
  },
  {
    q: "店舗が複数あっても運用できますか？",
    a: "複数店舗・複数スタッフでの運用に対応しています。対応履歴や顧客情報を組織横断で共有できます。",
  },
  {
    q: "導入までどのくらいの期間がかかりますか？",
    a: "要件や設計範囲によって異なります。まずはお問い合わせいただき、運用イメージをすり合わせるところから始まります。",
  },
  {
    q: "専門知識がなくても運用できますか？",
    a: "現場スタッフが使いやすい設計を重視しています。初期設定の支援や運用フローの設計までサポートします。",
  },
];
